import { getPublicAvailableToConfigure } from '@/api/settings-api'
import { useEffect, useMemo, useState } from 'react'
import { PbConfig } from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import {
  ShippingConfiguration,
  findReasonKeys,
  toFrontendFunctions
} from './use-available-shipping-rules'
import { usePortalTranslate } from './use-portal-translate'
import { Country } from '@/domain/countries'

const NO_CONFIGURATION: ShippingConfiguration = {
  name: '',
  params: [],
  functions: []
}

export function useAvailablePublicShippingRules(countries: Country[]) {
  const { transDefault } = usePortalTranslate({})
  const defaultCopies = useAppSelector(
    (store) => store.settingsApi.defaultCopies.response
  )

  const [configurations, setConfigurations] = useState<PbConfig[]>([])
  const [loadingAvailableRules, setLoadingAvailableRules] = useState(false)

  useEffect(() => {
    async function getAvailableRules() {
      if (countries.length === 0) return

      try {
        setLoadingAvailableRules(true)
        const response = await getPublicAvailableToConfigure('random')
        setConfigurations(response.configurations ?? [])
      } catch (error) {
        console.warn(error)
      } finally {
        setLoadingAvailableRules(false)
      }
    }

    getAvailableRules()
  }, [countries])

  // Reason labels come from the default copies, which can arrive after the rules.
  const availableConfiguration = useMemo(
    () =>
      configurations.length
        ? toFrontendFunctions(configurations, countries, transDefault)
        : NO_CONFIGURATION,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- transDefault is rebuilt each render and reads defaultCopies
    [configurations, countries, defaultCopies]
  )
  const reasonKeys = useMemo(
    () => findReasonKeys(configurations),
    [configurations]
  )

  return { availableConfiguration, loadingAvailableRules, reasonKeys }
}
